import { useMemo } from "react";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import {
	type DecimalPrecision,
	formatCurrency as formatCurrencyAt,
	formatWithSeparators as formatWithSeparatorsAt,
	format as formatAt,
	normalize as normalizeAt,
	resolvePrecision,
	stepForPrecision,
	toLineAmount as toLineAmountAt,
	toTotalAmount as toTotalAmountAt,
	TOTAL_PRECISION,
	UNIT_COST_MIN_PRECISION,
} from "@/utils/decimal";

/** Nullish input renders as zero; most call sites read off optional API fields. */
type Nullable = number | string | null | undefined;

const toNum = (value: Nullable): number => {
	const n = typeof value === "number" ? value : Number(value);
	return Number.isFinite(n) ? n : 0;
};

/** Company decimal precision with formatting helpers bound to it. */
export const useDecimalPrecision = () => {
	const { settings, isLoading } = useCompanySettings();

	const precision: DecimalPrecision = resolvePrecision(
		settings?.decimalPrecision,
	);

	return useMemo(
		() => ({
			precision,
			isLoadingPrecision: isLoading,

			/** Rounds a computed line value, keeping storage precision. */
			round: (value: Nullable) => toLineAmountAt(toNum(value)),
			/** Rounds a document total to 2dp. */
			roundTotal: (value: Nullable) => toTotalAmountAt(toNum(value)),
			normalize: (value: unknown) => normalizeAt(value, precision),
			format: (value: Nullable) => formatAt(toNum(value), precision),
			/** Formats a per-unit cost, at `max(precision, 3)`. */
			formatCost: (value: Nullable) =>
				formatAt(toNum(value), Math.max(precision, UNIT_COST_MIN_PRECISION)),
			/** Thousands-separated, e.g. "1,234.50". */
			formatAmount: (value: Nullable) =>
				formatWithSeparatorsAt(toNum(value), precision),
			/** Thousands-separated document total, always 2dp. */
			formatTotalAmount: (value: Nullable) =>
				formatWithSeparatorsAt(toNum(value), TOTAL_PRECISION),
			/** Currency string, e.g. "RM1,234.50". */
			formatCurrency: (value: Nullable, symbol = "RM") =>
				formatCurrencyAt(toNum(value), precision, symbol),
			/** `step` attribute for numeric inputs. */
			step: stepForPrecision(precision),
		}),
		[precision, isLoading],
	);
};
